import { ExpansionPanel, ExpansionPanelSummary, Grid } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import ExpandMore from '@material-ui/icons/ExpandMore';
import RightArrow from '@serko/zui-icons/es/KeyboardArrowRightIcon';
import Typography from '@serko/zui/es/Typography';
import React, { FunctionComponent } from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import pure from 'recompose/pure';
import messages from '../../intl/messages';
import { SegmentTypes } from '../../models/itinerary';
import { AppState } from '../../store/model';
import {
  isNew,
  shouldShowPricing,
} from '../../store/reducers/itinerary/selectors';
import { findSegment } from '../../store/reducers/segment-details/selectors';
import { SegmentIcon } from '../SegmentIcon';

interface OwnProps {
  segmentId: number;
  onClick(): void;
}

interface StateProps {
  options: number;
  price: string;
  type: SegmentTypes;
  title: string;
  details: string;
  showPrice: boolean;
  show: boolean;
}

interface OverviewProps
  extends OwnProps,
    StateProps,
    WithStyles<typeof styles>,
    InjectedIntlProps {}

const OverviewBase: FunctionComponent<OverviewProps> = ({
  classes,
  details,
  intl: { formatMessage },
  options: numberOfOptions,
  onClick,
  price,
  show,
  showPrice,
  title,
  type,
}) => (
  <ExpansionPanel onClick={onClick} expanded={false}>
    <ExpansionPanelSummary
      expandIcon={<ExpandMore className={classes.expandMore} />}
    >
      <Grid container spacing={16} wrap="nowrap">
        <Grid
          item
          className={classes[`icon${type}` as TypeClassKey]}
          justify="center"
        >
          <SegmentIcon type={type} />
        </Grid>
        <Grid item container direction="column">
          <Grid item>
            <Typography variant="subheading2">{title}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="body1" color="textSecondary">
              {details}
            </Typography>
          </Grid>
          <Grid item container spacing={16} wrap="nowrap">
            <Grid item xs={12}>
              {show && (
                <Button
                  size="small"
                  variant="text"
                  color="primary"
                  classes={{ root: classes.button, label: classes.buttonLabel }}
                >
                  {formatMessage(messages.options, {
                    numberOfOptions,
                  })}
                  <RightArrow className={classes.iconSmall} />
                </Button>
              )}
            </Grid>
            {!!showPrice && (
              <Grid item container xs alignItems="center">
                <Grid item>
                  <Typography variant="body2">{price}</Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </ExpansionPanelSummary>
  </ExpansionPanel>
);

const mapStateToProps = (
  state: AppState,
  { segmentId }: OverviewProps,
): StateProps => {
  const { title, details, price, type, options } = findSegment(segmentId)(
    state,
  );

  return {
    title,
    details,
    price,
    type,
    options,
    showPrice: shouldShowPricing(state),
    show: isNew(state),
  };
};

// styled
type TypeClassKey = 'iconAir' | 'iconCar' | 'iconHotel';
const styles = (theme: Theme) =>
  createStyles({
    expandMore: {
      color: theme.zeno.icons.dark.active,
    },
    button: {
      textTransform: 'none',
      marginLeft: -theme.spacing.unit,
    },
    buttonLabel: {
      whiteSpace: 'nowrap',
    },
    iconAir: {
      color: theme.zeno.travelModes.air.main,
    },
    iconCar: {
      color: theme.zeno.travelModes.vehicle.main,
    },
    iconHotel: {
      color: theme.zeno.travelModes.accommodation.main,
    },
    iconSmall: {
      fontSize: 20,
      marginRight: -theme.spacing.unit,
    },
  });

const enhance = compose<OverviewProps, OwnProps>(
  pure,
  injectIntl,
  connect(mapStateToProps),
  withStyles(styles),
);

/**
 * Segment overview (only used on mobile screens)
 *
 * Clicking on this overview will open the segment detail modal
 */
export const Overview = enhance(OverviewBase);

Overview.displayName = 'Overview';
